import { ProductTileProps, ProductTileMediumProps } from '@pb/components'

export enum ProductDetailContainerPadding {
  smallContainerPadding = 'smallContainerPadding',
  largeContainerPadding = 'largeContainerPadding',
}

export enum TitleTag {
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
}

interface Image {
  imageType?: string
  format?: string
  id?: string
  url: string
  baseUrl?: string
}

interface Price {
  currencyIso: string
  value: number
  priceType: string
  formattedValue: string
  type: string
}

interface Color {
  code: string
  text: string
}

interface OnSiteAttributes {
  productVideo: boolean
}

interface StockLevelStatus {
  code: string
  type: string
}

interface Stock {
  stockLevelStatus: StockLevelStatus
  stockLevel: number
}

interface Size {
  code: string
  sizeCode: string
  name: string
  market: string
  sizeScaleCode: string
}

export interface Variant {
  code: string
  stock: Stock
  size: Size
  fewPieceLeft: boolean
  selected: boolean
}

interface Marker {
  text: string
  type: string
  legalText: string
  colorCode: string
}

interface BrandsData {
  brandName: string
  subBrandNames?: string[]
  externalBrandColorID?: string
  external: boolean
}

interface ComparativePrice {
  comparativeQuantity: string,
  formattedPrice: string
  quantity: string
}

export interface FavouritesEntries {
  image: Image
  normalPicture: Image
  rootCategoryPath: string
  ordinaryPrice: Price
  priorPrice?: Price
  comparativePrice?: ComparativePrice
  quantity?: string
  color: Color
  name: string
  internalName: string
  sellingAttribute: string
  code: string
  baseProductCode: string
  articleCode: string
  inStock: boolean
  findInStore: boolean
  showPrice: boolean
  isYellowPriceAvailable: boolean
  categoryCode: string
  priceType: string
  productUrl: string
  showClubMarker: boolean
  fewPieceLeft: boolean
  inStoreStock: boolean
  comingSoon: boolean
  listPrice: string
  energyClass: string
  energyClassInterval: string
  energyClassCode: string
  energyClassIntervalCode: string
  onSiteAttributes: OnSiteAttributes
  variants: Variant[]
  favoriteProductMarker: Marker
  favoriteNonProductMarker: Marker
  discountedPrice: Price
  sizeScaleProductType: string
  brand: string
  brandsData: BrandsData
  preAccessGroups: string
  preAccessEndDate: string
  preAccessStartDate: string
}

export interface MiniCartData {
  productUrl?: string
  imgUrl?: string
  imgAlt?: string
  itemName?: string
  itemVariantCode?: string
  itemSize?: string
  itemOrdinaryPrice?: string
  itemOrdinaryPriceValue?: number
  itemPriorPrice?: string
  itemRedPrice?: string
  itemRedPriceValue?: number
  itemYellowPrice?: string
  itemYellowPriceValue?: number
  itemColor?: string
  fewPieceLeft?: boolean
  miniMiniCartLabel?: string
  clubPriceLabel?: string
  quantityLabel?: string
  sizeLabel?: string
  colorLabel?: string
  fewPiecesLeftLabel?: string
  errorLabel?: string
}

export interface AddToBagVerificationLabels {
  errorLabel?: string
  shoppingBagLabel?: string
  goToShoppingBagLabel?: string
  addedToBagLabel?: string
  closeLabel?: string
}

export interface ErrorProps {
  showError?: boolean
  errorMessage?: string
  variantCode?: string | number
  errorGroup?: string
  displayMode?: {
    isModal?: boolean
    isNotificationBar?: boolean
  }
}

export interface HiddenFavouritesData {
  code?: string
  title?: string
  removed: boolean
}

export interface EnergyLabel {
  energyClass?: 'A++' | 'A+' | 'A' | 'B' | 'C' | 'D' | 'E'
  energyLetter?: 'A' | 'B' | 'C' | 'D' | 'E'
  energySupSign?: '++' | '+'
  energyClassCode?: string
  energyClassIntervalCode?: string
  energyClassInterval?: string
  articleId?: string
  arrowDirection?: 'right' | 'left'
}

export interface ProductSizes {
  sizeCode: string
  sizeName: string
}

interface ProductTileFavouritesExtend {
  code: string
  baseProductCode?: string
  pdpUrl?: string
  redPrice?: string
  redPriceValue?: number
  yellowPrice?: string
  yellowPriceValue?: number
  regularPrice?: string
  regularPriceValue?: number
  currency?: string
  productVideo?: string
}

export type NormalizedFavourites = ProductTileProps & ProductTileFavouritesExtend
export type AddToBagVerificationData = MiniCartData

export interface FavouritesResponse {
  data: {
    entries: FavouritesEntries[] | []
  }
  ebEmptyValue?: boolean
  message?: string
}

interface OpeningHoursProps {
  day?: number
  name?: string
  opens?: string
  closes?: string
}

interface DepartmentWithConceptsProps {
  departmentId?: string
  name?: string
  concepts?: Array<{
    conceptId?: string
    name?: string
  }>
}

interface SizeProps {
  sizeCode?: string
  avaiQty?: number
  traffLightInd?: string
}

interface Store {
  storeCode?: string
  storeClass?: string
  name?: string
  phone?: string
  address: {
    streetName1?: string
    streetName2?: string
    postCode?: string
    postalAddress?: string
  }
  city?: string
  country?: string
  countryCode?: string
  longitude: Number
  latitude: Number
  timeZoneIndex?: Number
  openingHours?: OpeningHoursProps[]
  openingHourExceptions?: Array<{
    date?: string
    closedAllDay?: boolean
    opens?: string
    closes?: string
  }>
  departmentsWithConcepts?: DepartmentWithConceptsProps[]
  campaignConcepts?: Array<{}>
  sizes?: {
    size?: SizeProps[]
  }
  brand?: string
  channel?: string
  store?: string
  productid?: string
  artid?: string
  seasonlessResp?: string
}

export interface StoreProps {
  stores: Store[]
}

export type AddToBagVerificationProductData = {
  productTileData: ProductTileMediumProps
  quantity?: number
}

interface ArticleProps {
  articleCode: string
  availableSizeOptionCodes: string
  availableSizeOptions: string
  bluePrice: string
  brandName: string
  category: string
  hasPriceMarker: boolean
  imageModelAlt: string
  imageModelSrc: string
  imageProductAlt: string
  imageProductSrc: string
  isExternalBrand: boolean
  isNewProduct: boolean
  isOutOfStock: boolean
  marketingMarkerText: string
  marketingMarkerType: string
  pdpUrl: string
  redPrice: string
  regularPrice: string
  title: string
  yellowPrice: string
  mainCategory: string
}

export type ArticleResult = ArticleProps[]

export interface StoreSearchQuery {
  countryCode: string
  articleCode: string
  variantCode: string
  lat: number
  lng: number
  radiusInMeters: string
  maxNumberOfStores: string
  brand: string
  channel: string
}

export interface FavouritesState {
  items: NormalizedFavourites[] | []
  hiddenItems: HiddenFavouritesData[] | []
  storeSearchResults: any // TODO: Update from ANY type
  addToBagVerificationItem: MiniCartData | {}

  showAddToBagVerification: boolean

  addToBagVerificationError: {
    showError: boolean
    errorMessage: string
  }

  isFavouritesLoading: boolean
  isFavouritesPopulated: boolean
  isFavouritesLoadingFailed: boolean

  isHiddenFavouritesLoading: boolean
  isHiddenFavouritesLoaded: boolean
  isHiddenFavouritesLoadingFailed: boolean

  isFavouritesLoaded: boolean

  isStoreSearchResultsLoading: boolean
  isStoreSearchResultsPopulated: boolean
  isStoreSearchResultsLoadingFailed: boolean

  favouritesError: {
    errorGroup: string
    showError: boolean
    variantCode?: string | number
    displayMode: {
      isModal: boolean
      isNotificationBar: boolean
    }
  }

  itemSizeGuideResult: string
}
